import { WritableSignal } from "@angular/core";

import { setToPath, getFromPath } from "./set-get-path.function";
import { ensureImpl } from "./ensure-impl.function";
import { SignalTree } from "../types/signal-tree.type";
import { ensureMergeImpl } from "./ensure-merge-impl.function";

export function createNode<T>(
    root$$: WritableSignal<any>,
    path: string[]
): SignalTree<T> {

    const node = (() => getFromPath(root$$(), path)) as SignalTree<T>;

    // Een leeg pad betekent "de root zelf". setToPath kan dat niet aan: die pakt path[length-1]
    // (undefined) als sleutel en schrijft dan naar state['undefined'], waarna de echte root op zijn
    // beginwaarde blijft staan — stille corruptie, want er komt geen fout. Vandaar hier afvangen.
    node.set = (value: T) => {
        root$$.update(state => {
            if (path.length === 0) return structuredClone(value);

            const clone = structuredClone(state);
            setToPath(clone, path, value);
            return clone;
        });
    };

    node.update = (fn: (v: T) => T) => {
        root$$.update(state => {
            if (path.length === 0) return structuredClone(fn(structuredClone(state)));

            const clone = structuredClone(state);
            const current = getFromPath(clone, path);
            setToPath(clone, path, fn(current));
            return clone;
        });
    };

    node.delete = () => {
        root$$.update(state => {
            const clone = structuredClone(state);
            const parent = getFromPath(clone, path.slice(0, -1));

            if (parent) {
                delete parent[path[path.length - 1]];
            }

            return clone;
        });
    };

    // ✅ altijd beschikbaar
    node.ensure = (factory: () => T) =>
        ensureImpl(node, factory);

    // ✅ alleen runtime attach (type al veilig!)
    (node as any).ensureMerge = ((factory: () => T) => {
        return ensureMergeImpl(node as SignalTree<any>, factory as () => object) as SignalTree<T>;
    }) as T extends object
            ? (factory: () => T) => SignalTree<T>
            : never;

    return node;
}